import React, { useRef, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Animated, Platform } from 'react-native';
import { useTheme } from '../../../theme/ThemeProvider';
import { Icon } from '@iconify/react';
import { globalFontStyle } from '../../../assets';
import { globalConfigurableData } from '../../../utils/globalConstant';

/**
 * A modern, reusable Accordion component inspired by shadcn design.
 * 
 * Features:
 * - Smooth height animations
 * - Customizable triggers/headers
 * - Built-in chevron icon with rotation animation
 * - Theme integration
 * - Support for both controlled and uncontrolled states
 * 
 * Default Behavior:
 * - Uses tabler:chevron-down icon for expand/collapse
 * - Rotates icon 180° on expand
 * - Animates content height
 * 
 * @example
 * // Basic usage
 * <Accordion
 *   label="Section Title"
 *   content={<YourContent />}
 * />
 * 
 * // With custom trigger
 * <Accordion
 *   trigger={<CustomTrigger />}
 *   content={<CustomContent />}
 *   defaultValue={true}
 * />
 * 
 * @param {Object} props
 * @param {string} props.type - Type of accordion ('single' or 'multiple')
 * @param {boolean} props.defaultValue - Default expanded state
 * @param {boolean} props.collapsible - Whether the accordion can be collapsed
 * @param {React.ReactNode} props.trigger - Custom trigger/header component
 * @param {React.ReactNode} props.content - Content to show when expanded
 * @param {string} props.label - Text label for the header (if no custom trigger)
 * @param {Object} props.style - Additional styles for the container
 * @param {Object} props.triggerStyle - Additional styles for the trigger
 * @param {Object} props.contentStyle - Additional styles for the content container
 * @param {Function} props.onValueChange - Callback when value changes (expanded: boolean) => void
 * @param {boolean} props.value - Controlled value for the accordion
 * @param {string} props.iconColor - Color for the chevron icon (defaults to theme color)
 */
export const Accordion = ({
  type = 'single',
  defaultValue,
  collapsible = true,
  trigger,
  content,
  label,
  style,
  triggerStyle,
  contentStyle,
  onValueChange,
  value: controlledValue,
  iconColor,
}) => {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(defaultValue || false);
  const animatedHeight = useRef(new Animated.Value(defaultValue ? 1 : 0)).current;
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  const isControlled = controlledValue !== undefined;
  const expanded = isControlled ? controlledValue : isExpanded;

  React.useEffect(() => {
    if (defaultValue) {
      Animated.timing(animatedHeight, {
        toValue: 1,
        duration: 0,
        useNativeDriver: false,
      }).start();
    }
  }, []);

  const toggleAccordion = () => {
    if (!collapsible && expanded) return;

    const newValue = !expanded;
    if (!isControlled) {
      setIsExpanded(newValue);
    }
    
    onValueChange?.(newValue);

    Animated.timing(animatedHeight, {
      toValue: newValue ? 1 : 0,
      duration: 200,
      useNativeDriver: false,
    }).start();
  };

  const styles = StyleSheet.create({
    container: {
      overflow: 'hidden',
      backgroundColor: theme?.colors?.background || 'transparent',
      ...style,
    },
    trigger: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 16,
      backgroundColor: 'transparent',
      ...triggerStyle,
    },
    triggerText: {
      ...globalFontStyle.appFontSubTextBold,
      color: theme?.colors?.text?.primary || '#000000',
      flex: 1,
    },
    content: {
      overflow: 'hidden',
      ...contentStyle,
    },
    contentInner: {
    },
    chevronIcon: {
      width: 20,
      height: 20,
    }
  });

  const renderChevron = () => (
    <Animated.View
      style={{
        transform: [{
          rotate: animatedHeight.interpolate({
            inputRange: [0, 1],
            outputRange: ['0deg', '180deg'],
          }),
        }],
      }}
    >
      <Icon
        icon="tabler:chevron-down"
        style={styles.chevronIcon}
        color={iconColor || theme?.colors?.text?.primary || '#000000'}
      />
    </Animated.View>
  );

  const renderTrigger = () => {
    if (trigger) {
      return (
        <View style={styles.trigger}>
          {trigger}
          {renderChevron()}
        </View>
      );
    }

    return (
      <View style={styles.trigger}>
        <Text style={styles.triggerText}>{label}</Text>
        {renderChevron()}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <TouchableOpacity
        onPress={toggleAccordion}
        activeOpacity={0.7}
      >
        {renderTrigger()}
      </TouchableOpacity>
      
      <Animated.View
        style={[
          styles.content,
          {
            height: animatedHeight.interpolate({
              inputRange: [0, 1],
              outputRange: [0, contentHeight],
            }),
          },
        ]}
      >
        <View
          ref={contentRef}
          onLayout={(event) => {
            const height = event.nativeEvent.layout.height;
            setContentHeight(height);
          }}
          style={styles.contentInner}
        >
          {content}
        </View>
      </Animated.View>
    </View>
  );
};

export default Accordion; 